import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import UserAvatar from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INR, fmtDate } from "@/lib/format";
import { ChevronLeft, Plus, Building2, Mail, Phone, MapPin, Globe, IndianRupee, FileDown, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const SERVICES = ["Meta Ads", "Google Ads", "Social Media Management", "Website Development", "Website Management", "SEO", "Telecalling", "E-commerce Management", "Graphic Designing", "Video Editing", "Content Creation", "Marketing Consulting", "Branding", "Other"];
const BILLING = ["One Time", "Monthly Retainer", "Quarterly", "Annual", "Milestone Based", "Custom"];
const CLIENT_STATUSES = ["Lead", "Proposal Sent", "Negotiation", "Active", "On Hold", "Completed", "Lost", "Inactive"];

export default function ClientProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const financeOk = user?.finance_access || user?.role === "super_admin" || user?.role === "admin";
  const canEdit = user?.role === "super_admin" || user?.role === "admin";
  const isSuper = user?.role === "super_admin";

  const [c, setC] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [users, setUsers] = useState([]);
  const [addSvcOpen, setAddSvcOpen] = useState(false);
  const [svcForm, setSvcForm] = useState({ service_name: "", billing_type: "Monthly Retainer", original_quote: 0, negotiated_price: 0, tax_percent: 18, notes: "" });

  const load = async () => {
    const [cli, sv, pr, tk, dl, ac, u] = await Promise.all([
      api.get(`/clients/${id}`),
      api.get("/client-services", { params: { client_id: id } }),
      api.get("/projects", { params: { client_id: id } }),
      api.get("/tasks", { params: { client_id: id, limit: 200 } }),
      api.get("/deliverables", { params: { client_id: id } }),
      api.get("/activity", { params: { entity_type: "client", entity_id: id } }),
      api.get("/users"),
    ]);
    setC(cli.data); setServices(sv.data); setProjects(pr.data); setTasks(tk.data);
    setDeliverables(dl.data); setActivity(ac.data); setUsers(u.data);
    if (financeOk) {
      try { const p = await api.get("/payments", { params: { client_id: id } }); setPayments(p.data); } catch (_) {}
    }
  };
  useEffect(() => { load(); }, [id]);

  const addService = async () => {
    if (!svcForm.service_name) { toast.error("Choose a service"); return; }
    try {
      await api.post("/client-services", { ...svcForm, client_id: id });
      toast.success("Service added"); setAddSvcOpen(false);
      setSvcForm({ service_name: "", billing_type: "Monthly Retainer", original_quote: 0, negotiated_price: 0, tax_percent: 18, notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = () => {
    if (!c) return;
    setEditForm({
      company_name: c.company_name || "", client_name: c.client_name || "", contact_person: c.contact_person || "",
      phone: c.phone || "", whatsapp: c.whatsapp || "", email: c.email || "", website: c.website || "",
      address: c.address || "", city: c.city || "", state: c.state || "", country: c.country || "India",
      gst: c.gst || "", industry: c.industry || "", lead_source: c.lead_source || "",
      account_manager_id: c.account_manager_id || "", status: c.status || "Lead", notes: c.notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editForm.company_name.trim()) { toast.error("Company name is required"); return; }
    try {
      await api.put(`/clients/${id}`, editForm);
      toast.success("Client updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    if (deleteConfirmName.trim() !== c.company_name) {
      toast.error("Confirmation text does not match the company name.");
      return;
    }
    try {
      const r = await api.delete(`/clients/${id}`, { params: { hard: true } });
      const casc = r.data?.cascaded || {};
      toast.success(`Client deleted permanently. Removed ${Object.values(casc).reduce((a, b) => a + b, 0)} related records.`);
      nav("/clients");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!c) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const paymentPct = financeOk && c.contract_value > 0 ? Math.round((c.received / c.contract_value) * 100) : 0;
  const pendingTasks = tasks.filter((t) => !["Completed", "Cancelled"].includes(t.status));

  return (
    <div className="space-y-6" data-testid="client-profile-page">
      <div className="flex items-center justify-between">
        <button onClick={() => nav("/clients")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><ChevronLeft size={14} />Back to clients</button>
        <div className="flex items-center gap-2">
          {isSuper && (
            <>
              <Button data-testid="client-edit-btn" variant="outline" size="sm" onClick={openEdit}><Pencil size={13} className="mr-1" />Edit</Button>
              <Button data-testid="client-delete-btn" variant="outline" size="sm" onClick={() => { setDeleteConfirmName(""); setDeleteOpen(true); }} className="text-destructive hover:text-destructive"><Trash2 size={13} className="mr-1" />Delete</Button>
            </>
          )}
          <Button data-testid="client-export-btn" variant="outline" size="sm" onClick={() => window.open(`/clients/${id}/export`, "_blank")}>
            <FileDown size={14} className="mr-1" />Export 360°
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: sticky client card */}
        <div className="lg:col-span-4">
          <div className="border border-border rounded-md bg-card p-5 lg:sticky lg:top-20">
            <div className="w-12 h-12 rounded-md bg-accent flex items-center justify-center mb-3"><Building2 size={20} /></div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{c.client_id}</div>
            <h2 className="text-2xl font-black tracking-tight leading-tight">{c.company_name}</h2>
            <div className="mt-1"><StatusBadge value={c.status} /></div>

            <div className="mt-4 space-y-2 text-sm">
              {c.contact_person && <div className="flex items-center gap-2 text-muted-foreground"><span className="font-mono text-[10px] uppercase w-16">Contact</span><span className="text-foreground">{c.contact_person}</span></div>}
              {c.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone size={12} />{c.phone}</div>}
              {c.email && <div className="flex items-center gap-2 text-muted-foreground"><Mail size={12} />{c.email}</div>}
              {c.website && <div className="flex items-center gap-2 text-muted-foreground"><Globe size={12} />{c.website}</div>}
              {(c.city || c.state) && <div className="flex items-center gap-2 text-muted-foreground"><MapPin size={12} />{[c.city, c.state].filter(Boolean).join(", ")}</div>}
              {c.gst && <div className="flex items-center gap-2 text-muted-foreground"><span className="font-mono text-[10px] uppercase w-16">GST</span><span className="text-foreground font-mono">{c.gst}</span></div>}
            </div>

            {financeOk && (
              <div className="mt-5 pt-4 border-t border-border">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Finance</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="border border-border rounded-md p-2">
                    <div className="text-[9px] font-mono uppercase text-muted-foreground">Contract</div>
                    <div className="font-display font-black text-sm">{INR(c.contract_value)}</div>
                  </div>
                  <div className="border border-border rounded-md p-2">
                    <div className="text-[9px] font-mono uppercase text-muted-foreground">Received</div>
                    <div className="font-display font-black text-sm text-emerald-600 dark:text-emerald-400">{INR(c.received)}</div>
                  </div>
                  <div className="border border-border rounded-md p-2">
                    <div className="text-[9px] font-mono uppercase text-muted-foreground">Outstanding</div>
                    <div className="font-display font-black text-sm text-primary">{INR(c.outstanding)}</div>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] font-mono uppercase text-muted-foreground mb-1"><span>Payment progress</span><span>{paymentPct}%</span></div>
                  <Progress value={paymentPct} />
                </div>
              </div>
            )}

            <div className="mt-5 pt-4 border-t border-border grid grid-cols-3 gap-2 text-center text-xs">
              <div><div className="font-black text-lg">{services.length}</div><div className="text-[10px] font-mono uppercase text-muted-foreground">Services</div></div>
              <div><div className="font-black text-lg">{projects.length}</div><div className="text-[10px] font-mono uppercase text-muted-foreground">Projects</div></div>
              <div><div className="font-black text-lg">{pendingTasks.length}</div><div className="text-[10px] font-mono uppercase text-muted-foreground">Open tasks</div></div>
            </div>
          </div>
        </div>

        {/* Right: tabs */}
        <div className="lg:col-span-8">
          <Tabs defaultValue="overview">
            <TabsList className="w-full justify-start overflow-x-auto">
              <TabsTrigger data-testid="tab-overview" value="overview">Overview</TabsTrigger>
              <TabsTrigger data-testid="tab-services" value="services">Services</TabsTrigger>
              <TabsTrigger data-testid="tab-projects" value="projects">Projects</TabsTrigger>
              <TabsTrigger data-testid="tab-tasks" value="tasks">Tasks</TabsTrigger>
              <TabsTrigger data-testid="tab-deliverables" value="deliverables">Deliverables</TabsTrigger>
              {financeOk && <TabsTrigger data-testid="tab-finance" value="finance">Finance</TabsTrigger>}
              <TabsTrigger data-testid="tab-activity" value="activity">Activity</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-5 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <StatCard title="Active services" body={services.filter((s) => s.status === "Active").length} />
                <StatCard title="Open tasks" body={pendingTasks.length} />
                <StatCard title="Active projects" body={projects.filter((p) => p.status !== "Completed" && p.status !== "Cancelled").length} />
                <StatCard title="Pending deliverables" body={deliverables.filter((d) => d.status !== "Completed" && d.status !== "Approved").length} />
              </div>
              {c.notes && (
                <div className="border border-border rounded-md p-4 bg-card">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Notes</div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{c.notes}</div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="services" className="mt-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{services.length} services</div>
                {canEdit && (
                  <Sheet open={addSvcOpen} onOpenChange={setAddSvcOpen}>
                    <SheetTrigger asChild><Button size="sm" data-testid="add-service-btn"><Plus size={14} className="mr-1" />Add Service</Button></SheetTrigger>
                    <SheetContent className="sm:max-w-md overflow-y-auto">
                      <SheetHeader><SheetTitle>Add Service</SheetTitle></SheetHeader>
                      <div className="mt-5 space-y-3">
                        <div>
                          <Label className="font-mono text-[10px] uppercase">Service</Label>
                          <Select value={svcForm.service_name} onValueChange={(v) => setSvcForm({ ...svcForm, service_name: v })}>
                            <SelectTrigger data-testid="svc-form-name"><SelectValue placeholder="Choose service" /></SelectTrigger>
                            <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="font-mono text-[10px] uppercase">Billing type</Label>
                          <Select value={svcForm.billing_type} onValueChange={(v) => setSvcForm({ ...svcForm, billing_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{BILLING.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><Label className="font-mono text-[10px] uppercase">Original quote (₹)</Label><Input data-testid="svc-form-original" type="number" value={svcForm.original_quote} onChange={(e) => setSvcForm({ ...svcForm, original_quote: parseFloat(e.target.value || 0) })} /></div>
                          <div><Label className="font-mono text-[10px] uppercase">Negotiated (₹)</Label><Input data-testid="svc-form-negotiated" type="number" value={svcForm.negotiated_price} onChange={(e) => setSvcForm({ ...svcForm, negotiated_price: parseFloat(e.target.value || 0) })} /></div>
                        </div>
                        <div><Label className="font-mono text-[10px] uppercase">Tax %</Label><Input type="number" value={svcForm.tax_percent} onChange={(e) => setSvcForm({ ...svcForm, tax_percent: parseFloat(e.target.value || 0) })} /></div>
                        <div><Label className="font-mono text-[10px] uppercase">Notes</Label><Textarea value={svcForm.notes} onChange={(e) => setSvcForm({ ...svcForm, notes: e.target.value })} rows={2} /></div>
                        <Button data-testid="svc-form-submit" onClick={addService} className="w-full bg-primary hover:bg-primary/90">Add Service</Button>
                      </div>
                    </SheetContent>
                  </Sheet>
                )}
              </div>
              <div className="space-y-2">
                {services.length === 0 ? <div className="text-sm text-muted-foreground">No services yet.</div> :
                  services.map((s) => (
                    <div key={s.id} className="border border-border rounded-md p-4 bg-card flex items-center justify-between" data-testid={`service-row-${s.id}`}>
                      <div>
                        <div className="font-semibold">{s.service_name}</div>
                        <div className="text-xs text-muted-foreground font-mono">{s.billing_type} · {s.billing_cycle} · Start {fmtDate(s.start_date)}</div>
                      </div>
                      <div className="text-right">
                        {financeOk && <div className="font-display font-black">{INR(s.total_contract_value)}</div>}
                        <StatusBadge value={s.status} />
                      </div>
                    </div>
                  ))}
              </div>
            </TabsContent>

            <TabsContent value="projects" className="mt-5 space-y-2">
              {projects.length === 0 ? <div className="text-sm text-muted-foreground">No projects.</div> :
                projects.map((p) => (
                  <div key={p.id} className="border border-border rounded-md p-4 bg-card" data-testid={`project-row-${p.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-semibold">{p.name}</div>
                        <div className="text-xs text-muted-foreground font-mono">Ends {fmtDate(p.expected_end_date)}</div>
                      </div>
                      <div className="text-right"><StatusBadge value={p.status} /></div>
                    </div>
                    <div className="mt-3"><Progress value={p.progress || 0} /><div className="text-[10px] font-mono text-muted-foreground mt-1">{p.progress || 0}% complete · {p._tasks_completed}/{p._task_count} tasks</div></div>
                  </div>
                ))
              }
            </TabsContent>

            <TabsContent value="tasks" className="mt-5 space-y-2">
              {tasks.length === 0 ? <div className="text-sm text-muted-foreground">No tasks.</div> :
                tasks.slice(0, 30).map((t) => {
                  const assignee = users.find((u) => u.id === t.assignee_id);
                  return (
                    <div key={t.id} className="border border-border rounded-md p-3 bg-card flex items-center justify-between" data-testid={`ctask-row-${t.id}`}>
                      <div>
                        <div className="font-semibold text-sm">{t.name}</div>
                        <div className="text-[11px] font-mono text-muted-foreground">Due {fmtDate(t.due_date)} · {assignee?.name || "Unassigned"}</div>
                      </div>
                      <StatusBadge value={t.status} />
                    </div>
                  );
                })
              }
            </TabsContent>

            <TabsContent value="deliverables" className="mt-5 space-y-2">
              {deliverables.length === 0 ? <div className="text-sm text-muted-foreground">No deliverables.</div> :
                deliverables.map((d) => {
                  const pct = d.quantity_promised ? Math.round((d.quantity_completed / d.quantity_promised) * 100) : 0;
                  return (
                    <div key={d.id} className="border border-border rounded-md p-4 bg-card" data-testid={`deliv-row-${d.id}`}>
                      <div className="flex justify-between">
                        <div>
                          <div className="font-semibold">{d.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{d.frequency} · Due {fmtDate(d.due_date)}</div>
                        </div>
                        <StatusBadge value={d.status} />
                      </div>
                      <div className="mt-2 text-xs">{d.quantity_completed}/{d.quantity_promised} completed</div>
                      <div className="mt-1"><Progress value={pct} /></div>
                    </div>
                  );
                })
              }
            </TabsContent>

            {financeOk && (
              <TabsContent value="finance" className="mt-5 space-y-2">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <StatCard title="Contract" body={INR(c.contract_value)} />
                  <StatCard title="Received" body={INR(c.received)} />
                  <StatCard title="Outstanding" body={INR(c.outstanding)} accent />
                </div>
                <div className="border border-border rounded-md bg-card">
                  <div className="p-3 border-b border-border font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Payments</div>
                  {payments.length === 0 ? <div className="p-4 text-sm text-muted-foreground">No payments recorded.</div> :
                    payments.map((p) => (
                      <div key={p.id} className="p-3 flex items-center justify-between border-b border-border last:border-0" data-testid={`payment-row-${p.id}`}>
                        <div>
                          <div className="font-semibold flex items-center gap-2"><IndianRupee size={14} />{INR(p.amount)}</div>
                          <div className="text-xs text-muted-foreground font-mono">{p.method} · {fmtDate(p.payment_date)} · {p.reference}</div>
                        </div>
                        <StatusBadge value={p.status === "void" ? "Cancelled" : "Approved"} />
                      </div>
                    ))
                  }
                </div>
              </TabsContent>
            )}

            <TabsContent value="activity" className="mt-5">
              <div className="space-y-2">
                {activity.length === 0 ? <div className="text-sm text-muted-foreground">No activity yet.</div> :
                  activity.map((a) => (
                    <div key={a.id} className="border border-border rounded-md p-3 bg-card text-sm">
                      <div><span className="font-semibold">{a.actor_name}</span> <span className="text-muted-foreground">{a.action}</span> {a.entity_label}</div>
                      <div className="text-[11px] font-mono text-muted-foreground">{fmtDate(a.created_at)}</div>
                    </div>
                  ))
                }
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Edit client sheet — super admin only */}
      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          <SheetHeader><SheetTitle>Edit client</SheetTitle></SheetHeader>
          {editForm && (
            <div className="mt-5 space-y-3">
              <div><Label className="font-mono text-[10px] uppercase">Company name *</Label><Input data-testid="edit-company" value={editForm.company_name} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="font-mono text-[10px] uppercase">Contact person</Label><Input value={editForm.contact_person} onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">Primary contact</Label><Input value={editForm.client_name} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="font-mono text-[10px] uppercase">Phone</Label><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">WhatsApp</Label><Input value={editForm.whatsapp} onChange={(e) => setEditForm({ ...editForm, whatsapp: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="font-mono text-[10px] uppercase">Email</Label><Input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">Website</Label><Input value={editForm.website} onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} /></div>
              </div>
              <div><Label className="font-mono text-[10px] uppercase">Address</Label><Input value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><Label className="font-mono text-[10px] uppercase">City</Label><Input value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">State</Label><Input value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">Country</Label><Input value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="font-mono text-[10px] uppercase">GST</Label><Input value={editForm.gst} onChange={(e) => setEditForm({ ...editForm, gst: e.target.value })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">Industry</Label><Input value={editForm.industry} onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="font-mono text-[10px] uppercase">Lead source</Label><Input value={editForm.lead_source} onChange={(e) => setEditForm({ ...editForm, lead_source: e.target.value })} /></div>
                <div><Label className="font-mono text-[10px] uppercase">Account manager</Label>
                  <Select value={editForm.account_manager_id || "__none__"} onValueChange={(v) => setEditForm({ ...editForm, account_manager_id: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {users.filter((u) => u.role !== "member" && u.status === "active").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label className="font-mono text-[10px] uppercase">Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{CLIENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="font-mono text-[10px] uppercase">Notes</Label><Textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
              <Button data-testid="edit-save-btn" onClick={saveEdit} className="w-full bg-primary hover:bg-primary/90">Save changes</Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete this client permanently?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="border border-destructive/40 bg-destructive/5 rounded-md p-3 text-destructive">
              This will permanently remove <span className="font-bold">{c?.company_name}</span> and cascade-delete all related records:
              <ul className="list-disc list-inside mt-2 text-xs">
                <li>{services.length} services · {projects.length} projects · {tasks.length} tasks</li>
                <li>{deliverables.length} deliverables · {payments.length} payments · quotations</li>
              </ul>
              <div className="mt-2 text-xs">This action cannot be undone.</div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase">Type <span className="text-foreground font-bold">{c?.company_name}</span> to confirm</Label>
              <Input data-testid="delete-confirm-input" value={deleteConfirmName} onChange={(e) => setDeleteConfirmName(e.target.value)} placeholder={c?.company_name} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button
              data-testid="delete-confirm-btn"
              onClick={confirmDelete}
              disabled={deleteConfirmName.trim() !== c?.company_name}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground disabled:opacity-40"
            >
              <Trash2 size={13} className="mr-1" />Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ title, body, accent }) {
  return (
    <div className={`border border-border rounded-md p-4 bg-card ${accent ? "border-primary/40 bg-primary/5" : ""}`}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{title}</div>
      <div className="font-display text-2xl font-black mt-1">{body}</div>
    </div>
  );
}
